import { useMemo, useState } from "react";
import {
  SYSTEMS,
  goToSystem,
  type SystemDef,
  type SystemModule,
} from "../integration";
import type { User } from "../types";
import { isAdminSupremo } from "../access";
import SystemCard from "./SystemCard";

export default function Workspace({ user }: { user: User }) {
  const [q, setQ] = useState("");

  /** Abre um sistema; se `module` for informado, já na seção correspondente. */
  function open(s: SystemDef, module?: SystemModule) {
    goToSystem(s.url, user.usuario, module);
  }

  // Sistemas liberados para este usuário (o Admin Supremo vê todos).
  const allowed = useMemo(() => {
    if (isAdminSupremo(user.usuario)) return SYSTEMS;
    const set = new Set(user.sistemas ?? []);
    const liberados = SYSTEMS.filter((s) => set.has(s.id));
    // Sistemas só-visualização: atalhos só das abas liberadas (e nunca "Usuários").
    // Sessão salva antes da escolha por sistema só tinha a caixa geral: vale para todos.
    const leitura = new Set(user.sistemasLeitura ?? (user.somenteLeitura ? user.sistemas : []));
    return liberados.map((s) => {
      if (!leitura.has(s.id)) return s;
      const abas = user.abas?.[s.id];
      return {
        ...s,
        modules: s.modules.filter((m) => m.key !== "usuarios" && (!abas || abas.includes(m.key))),
      };
    });
  }, [user]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return allowed;
    return allowed.filter((s) =>
      [s.nome, s.descricao, ...s.modules.map((m) => m.label)]
        .join(" ")
        .toLowerCase()
        .includes(t),
    );
  }, [q, allowed]);

  return (
    <>
      <div className="content-head">
        <div className="ws-hero">
          <div>
            <h1>Área de Trabalho</h1>
            <p className="hello">
              Olá, <b>{user.nome}</b>! Escolha um sistema ou abra direto em um
              módulo.
            </p>
          </div>
          <span className="ws-pill">{allowed.length} sistemas</span>
        </div>

        <div className="ws-search">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar sistema ou módulo..."
            aria-label="Buscar sistema"
          />
          {q && (
            <button
              className="ws-search-clear"
              onClick={() => setQ("")}
              title="Limpar busca"
              aria-label="Limpar busca"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {allowed.length === 0 ? (
        <div className="ws-empty">
          Você ainda não tem sistemas liberados. Fale com o Admin Supremo para
          liberar seus acessos.
        </div>
      ) : filtered.length === 0 ? (
        <div className="ws-empty">Nenhum sistema encontrado para “{q}”.</div>
      ) : (
        <div className="systems">
          {filtered.map((s) => (
            <SystemCard key={s.id} system={s} onOpen={open} />
          ))}
        </div>
      )}
    </>
  );
}
